import React, { useState } from 'react';
import styled from 'styled-components';

import {
  CheckoutLabelGrouping,
  CheckoutLabel,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import { SmallLoader } from 'shared/components';
import { mediaQueries } from 'shared/styles';
import { GqlAllotmentInfo, GqlAllotmentDetail, AllotmentStatus } from 'types/graphql';

const FL_MMUR_REGISTRY_URL = 'https://knowthefactsmmj.com/registry/';

export function formatRemaining(remainingWithCart: number, unit: string): string {
  const rounded = Math.round(remainingWithCart * 100) / 100;
  if (unit === 'oz') {
    return `${rounded} oz`;
  }
  if (rounded >= 1000) {
    return `${rounded.toLocaleString()} mg`;
  }
  return `${rounded} mg`;
}

type ParentGroup = {
  parentCategory: string;
  children: GqlAllotmentDetail[];
};

function isDetail(d: GqlAllotmentDetail | null): d is GqlAllotmentDetail {
  return d !== null;
}

function groupByParentCategory(details: Array<GqlAllotmentDetail | null>): ParentGroup[] {
  const groups = new Map<string, GqlAllotmentDetail[]>();
  const groupOrder: string[] = [];

  details.filter(isDetail).forEach((detail) => {
    const parent = detail.parentCategory ?? detail.category ?? 'Other';
    if (!groups.has(parent)) {
      groups.set(parent, []);
      groupOrder.push(parent);
    }
    groups.get(parent)?.push(detail);
  });

  return groupOrder.map((parentCategory) => ({
    parentCategory,
    children: groups.get(parentCategory) ?? [],
  }));
}

type MMURAllotmentProps = {
  allotmentInfo: GqlAllotmentInfo | null;
  loading: boolean;
};

export function MMURAllotment({ allotmentInfo, loading }: MMURAllotmentProps): JSX.Element | null {
  if (loading) {
    return (
      <CheckoutLabelGrouping>
        <CheckoutLabel>
          <CheckoutSectionHeading>MMUR Limits</CheckoutSectionHeading>
        </CheckoutLabel>
        <CheckoutBody>
          <AllotmentCard>
            <div data-testid='mmur-allotment-loader'>
              <SmallLoader height={40} color='#646d72' />
            </div>
          </AllotmentCard>
        </CheckoutBody>
      </CheckoutLabelGrouping>
    );
  }

  if (!allotmentInfo) {
    return null;
  }

  const groups = groupByParentCategory(allotmentInfo.details ?? []);

  return (
    <CheckoutLabelGrouping>
      <CheckoutLabel>
        <CheckoutSectionHeading>MMUR Limits</CheckoutSectionHeading>
      </CheckoutLabel>
      <CheckoutBody>
        <AllotmentCard>
          <CategoryList>
            {groups.map((group) => (
              <ParentCategoryGroup key={group.parentCategory} group={group} />
            ))}
          </CategoryList>
          <Divider />
          <HelperText>
            View more details about your MMUR limits by logging into{' '}
            <RegistryLink href={FL_MMUR_REGISTRY_URL} target='_blank' rel='noopener noreferrer'>
              Florida Health Medical Marijuana Use Registry
            </RegistryLink>
          </HelperText>
        </AllotmentCard>
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
}

function ParentCategoryGroup({ group }: { group: ParentGroup }): JSX.Element {
  const isStandalone = group.children.length === 1 && group.children[0].parentCategory === group.children[0].category;

  if (isStandalone) {
    const detail = group.children[0];
    return (
      <CategoryRow>
        <CategoryName exceeded={detail.status === AllotmentStatus.exceeded}>{detail.category}</CategoryName>
        <CategoryRemaining exceeded={detail.status === AllotmentStatus.exceeded}>
          {detail.isUnlimited
            ? 'Approved'
            : `${formatRemaining(detail.remainingWithCart ?? 0, detail.unit ?? 'mg')} Remaining`}
        </CategoryRemaining>
      </CategoryRow>
    );
  }

  return <CollapsibleGroup group={group} />;
}

function CollapsibleGroup({ group }: { group: ParentGroup }): JSX.Element {
  const hasExceeded = group.children.some((d) => d.status === AllotmentStatus.exceeded);
  const [expanded, setExpanded] = useState(hasExceeded);

  return (
    <div>
      <ParentHeader
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(!expanded);
          }
        }}
        role='button'
        tabIndex={0}
        aria-expanded={expanded}
        data-testid={`parent-${group.parentCategory}`}
      >
        <ParentName>{group.parentCategory}</ParentName>
        <Chevron>{expanded ? '\u25B2' : '\u25BC'}</Chevron>
      </ParentHeader>
      {expanded && (
        <ChildList>
          {group.children.map((detail) => (
            <ChildRow key={detail.category}>
              <ChildName exceeded={detail.status === AllotmentStatus.exceeded}>{detail.category}</ChildName>
              <CategoryRemaining exceeded={detail.status === AllotmentStatus.exceeded}>
                {detail.isUnlimited
                  ? 'Approved'
                  : `${formatRemaining(detail.remainingWithCart ?? 0, detail.unit ?? 'mg')} Remaining`}
              </CategoryRemaining>
            </ChildRow>
          ))}
        </ChildList>
      )}
    </div>
  );
}

const AllotmentCard = styled.div`
  background: #fff;
  border: 1px solid #d8dee3;
  border-radius: 5px;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.07);
  padding: 20px;
`;

const HelperText = styled.p`
  color: #646d72;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.065px;
  margin: 0;
`;

const RegistryLink = styled.a`
  color: #0555ae;
  text-decoration: underline;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e3e7e9;
  margin: 20px 0;
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CategoryRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media ${mediaQueries.largePhone} {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const CategoryName = styled.span<{ exceeded: boolean }>`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: ${({ exceeded }) => (exceeded ? '#be0517' : '#2c3236')};
  width: 140px;
  flex-shrink: 0;
`;

const CategoryRemaining = styled.span<{ exceeded: boolean }>`
  font-size: 14px;
  line-height: 20px;
  color: ${({ exceeded }) => (exceeded ? '#be0517' : '#2c3236')};
  white-space: nowrap;
`;

const ParentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
`;

const ParentName = styled.span`
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #2c3236;
`;

const Chevron = styled.span`
  font-size: 10px;
  color: #646d72;
`;

const ChildList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 16px;
  margin-top: 8px;
`;

const ChildRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media ${mediaQueries.largePhone} {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const ChildName = styled.span<{ exceeded: boolean }>`
  font-size: 14px;
  line-height: 20px;
  color: ${({ exceeded }) => (exceeded ? '#be0517' : '#2c3236')};
  width: 140px;
  flex-shrink: 0;
`;
